/**
 * シークレット管理タブ（owner 限定）。
 * 仕様書: docs/01_spec/spec_common.md C-4.2
 *
 * 原則:
 *   - UI に値を表示しない（書込専用、password 型）
 *   - 書込は HTTPS Callable Function 経由のみ
 *   - 登録状態（boolean + 最終更新日）のみ Firestore の settings/secrets_status から表示
 */
import { useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { AlertCircle, CheckCircle2, Edit, ExternalLink, RotateCw } from 'lucide-react';
import { useState } from 'react';

import type { SecretKey } from '@goenhub/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { SECRET_KEYS, useSecretsStatus } from '@/hooks/useSettings';
import { functions } from '@/lib/firebase';

interface UpdateSecretRequest {
  secretKey: SecretKey;
  secretValue: string;
}

interface UpdateSecretResponse {
  ok: boolean;
  secretKey: SecretKey;
  version: string;
}

const updateSecretFn = httpsCallable<UpdateSecretRequest, UpdateSecretResponse>(
  functions,
  'updateSecret',
);

function formatDate(ts: unknown): string {
  if (!ts) return '—';
  // Firestore Timestamp or ISO
  const date =
    typeof ts === 'object' && ts && 'toDate' in ts && typeof ts.toDate === 'function'
      ? (ts as { toDate: () => Date }).toDate()
      : new Date(String(ts));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ja-JP');
}

export function SecretsManagementTab() {
  const { data: statuses, isLoading } = useSecretsStatus();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SecretKey | null>(null);
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const editingMeta = SECRET_KEYS.find((s) => s.key === editing);

  const handleSubmit = async () => {
    if (!editing || !value) return;
    setSubmitting(true);
    try {
      const res = await updateSecretFn({ secretKey: editing, secretValue: value });
      toast.success(`${editingMeta?.label} を更新しました（version: ${res.data.version}）`);
      setEditing(null);
      setValue('');
      // Firestore 側の settings/secrets_status が Callable で更新されたので再取得
      await queryClient.invalidateQueries({ queryKey: ['settings', 'secrets_status'] });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`更新に失敗しました: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="max-w-3xl">
      <div className="rounded bg-[var(--accent-subtle)] border border-[var(--accent)] p-4 mb-6 flex gap-3">
        <AlertCircle className="size-4 text-[var(--accent)] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-[var(--text-primary)] leading-relaxed">
          <strong>シークレットの取扱い</strong>
          <ul className="list-disc list-inside mt-1 space-y-0.5 text-[var(--text-secondary)]">
            <li>入力後、値は Google Secret Manager に保存されます（Firestore には保存しない）</li>
            <li>保存後の値はどこからも取得できません（書込専用）。必要に応じて再登録してください</li>
            <li>変更は監査ログ (secret_audit_logs) に記録され、owner 全員に Gmail 通知が届きます</li>
            <li>1 時間あたり 5 回の更新制限があります</li>
            <li>
              <strong className="text-[var(--accent-amber)]">ローテート後は対象 Cloud Function の再デプロイが必要です</strong>
              （Firebase Functions v2 はデプロイ時のバージョンに固定されるため）
            </li>
          </ul>
        </div>
      </div>

      <div className="space-y-2">
        {SECRET_KEYS.map((meta) => {
          const status = statuses?.[meta.key];
          const configured = status?.configured ?? false;
          return (
            <div
              key={meta.key}
              className="flex items-center gap-4 p-4 rounded bg-[var(--bg-surface)] border border-[var(--border)]"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-semibold text-[13px]">{meta.label}</span>
                  {configured ? (
                    <Badge variant="approved">
                      <CheckCircle2 className="size-3" /> 設定済
                    </Badge>
                  ) : (
                    <Badge variant="danger">
                      <AlertCircle className="size-3" /> 未設定
                    </Badge>
                  )}
                  <a
                    href={meta.issuerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-0.5"
                    title={`${meta.issuerName} で発行`}
                  >
                    {meta.issuerName} <ExternalLink className="size-2.5" />
                  </a>
                </div>
                <p className="text-xs text-[var(--text-secondary)]">{meta.description}</p>
                {status?.updatedAt && (
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    最終更新: {formatDate(status.updatedAt)}
                  </p>
                )}
                {meta.affectedFunctions && meta.affectedFunctions.length > 0 && (
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">
                    🔁 ローテート後に再デプロイ要: {meta.affectedFunctions.length} 個の Cloud Function
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setEditing(meta.key);
                  setValue('');
                }}
              >
                {configured ? <RotateCw /> : <Edit />}
                {configured ? 'ローテート' : '登録'}
              </Button>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingMeta?.label}</DialogTitle>
            <DialogDescription>
              値は Google Secret Manager に保存され、Firestore には書き込まれません。
            </DialogDescription>
          </DialogHeader>

          {editingMeta && (
            <div className="rounded bg-[var(--bg-elevated)] border border-[var(--border)] p-3 text-xs space-y-2">
              <div>
                <span className="text-[var(--text-muted)]">発行元:</span>{' '}
                <a
                  href={editingMeta.issuerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--accent)] hover:underline inline-flex items-center gap-0.5"
                >
                  {editingMeta.issuerName} <ExternalLink className="size-2.5" />
                </a>
              </div>
              {editingMeta.affectedFunctions && editingMeta.affectedFunctions.length > 0 && (
                <div className="border-t border-[var(--border)] pt-2">
                  <p className="text-[var(--text-muted)] mb-1">
                    ⚠ ローテート後、以下の Cloud Function を再デプロイしてください:
                  </p>
                  <pre className="bg-[var(--bg-hover)] border border-[var(--border)] rounded p-2 text-[10px] overflow-x-auto whitespace-pre-wrap">
                    firebase deploy --only {editingMeta.affectedFunctions.map((f) => `functions:${f}`).join(',')} --project caud-goenhub-dev
                  </pre>
                </div>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="secret-value">値</Label>
            <Input
              id="secret-value"
              type="password"
              placeholder={editingMeta?.placeholder}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              入力後は再表示できません。必ず元データを別途管理してください。
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditing(null)}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || !value}
            >
              {submitting ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
